import Modal from "components/ui/Modal";
import PageLoader from "components/ui/PageLoader";
import { showToast } from "components/ui/toast";
import { GripVertical, Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "../utils/axios";
import EditableField from "components/ui/EditableField";
import Button from "components/ui/Button";

const SortableVideoCard = ({
  data,
  index,
  handleVideoDelete,
  handleVideoTitleUpdate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: data.videoUrl });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative max-w-[200px] sm:max-w-[240px] flex flex-col items-center bg-lightgray hover:bg-mediumgray py-2 rounded-md ${
        isDragging ? "z-10" : ""
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-800"
        style={{ touchAction: "none" }}
      >
        <GripVertical size={20} />
      </div>
      <span
        className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded"
        style={{ zIndex: 10 }}
      >
        {index + 1}
      </span>
      <Button
        className="absolute top-2 right-2 black-button p-1 z-[9999]"
        onClick={() => handleVideoDelete(data.videoUrl)}
      >
        <Trash2Icon size={16} />
      </Button>

      <video className="aspect-square rounded-md py-2" controls>
        <source src={data?.videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <EditableField
        value={data?.title}
        onSave={(title) => handleVideoTitleUpdate(title, data.videoUrl)}
        onClick={(e) => e.stopPropagation()}
        className="justify-center text-xs"
      />
    </div>
  );
};

const ShowVideos = ({ videos, openReorderModal }) => {
  const categories = [
    { name: "Start with Belief", key: "startWithBelief" },
    { name: "Beleef", key: "beleef" },
    { name: "Vision", key: "vision" },
    { name: "Induction", key: "induction" },
  ];

  return (
    <div className="mt-6 space-y-10">
      {categories.map((category) => (
        <div key={category.key}>
          <div className="flex justify-between items-center ">
            <h3 className="text-lg font-semibold">{category.name}</h3>
            <Button
              className="black-button"
              onClick={() => openReorderModal(category.key)}
            >
              Reorder {category.name}
            </Button>
          </div>
          <div className="mt-4 ">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-8">
              {videos[category.key]?.length > 0 ? (
                videos[category.key].map((data, index) => (
                  <div
                    key={data?.videoUrl}
                    className="rounded-md flex-shrink-0"
                  >
                    <video controls className="h-full min-h-96 rounded-md">
                      <source src={data?.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500">
                  No videos uploaded yet.
                </p>
              )}
            </div>
          </div>
          <hr className="mt-6 border-gray-300" />
        </div>
      ))}
    </div>
  );
};

const ReorderVideo = ({
  videos,
  handleReorder,
  handleSaveReorder,
  selectedCategory,
  handleVideoDelete,
  handleVideoTitleUpdate,
}) => {
  const categories = [
    { name: "Start with Belief", key: "startWithBelief" },
    { name: "Beleef", key: "beleef" },
    { name: "Vision", key: "vision" },
    { name: "Induction", key: "induction" },
  ];

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 2 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(PointerSensor, { activationConstraint: { distance: 2 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const items = videos[selectedCategory] || [];
    const oldIndex = items.findIndex((v) => v.videoUrl === active.id);
    const newIndex = items.findIndex((v) => v.videoUrl === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    handleReorder(selectedCategory, arrayMove(items, oldIndex, newIndex));
  };

  return (
    <div className="mt-6 space-y-10">
      {categories
        .filter((category) => category.key === selectedCategory)
        .map((category) => {
          const items = videos[category.key] || [];
          return (
            <div key={category.key}>
              <h3 className="text-lg font-semibold text-center">
                {category.name}
              </h3>

              {items.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={items.map((v) => v.videoUrl)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-2 gap-4 p-4 rounded-md">
                      {items.map((data, index) => (
                        <SortableVideoCard
                          key={data.videoUrl}
                          data={data}
                          index={index}
                          handleVideoDelete={handleVideoDelete}
                          handleVideoTitleUpdate={handleVideoTitleUpdate}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="text-center text-gray-500">
                  No videos uploaded yet.
                </p>
              )}

              <hr className="mt-4 mb-4 border-gray-300" />
              <div className="flex justify-center">
                <Button onClick={handleSaveReorder} className="black-button">
                  Save Order
                </Button>
              </div>
            </div>
          );
        })}
    </div>
  );
};

const Videos = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [video, setVideo] = useState({
    beleef: [],
    startWithBelief: [],
    vision: [],
    induction: [],
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState(null);

  const openReorderModal = (categoryKey) => {
    setSelectedTab(categoryKey);
    setIsOrderModalOpen(true);
  };

  const videoPreviewUrl = useMemo(() => {
    return file ? URL.createObjectURL(file) : null;
  }, [file]);

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/admin/video");
      if (response.data.success) {
        const videos = response.data.data;

        setVideo({
          beleef: videos.beleef,
          startWithBelief: videos.startWithBelief,
          vision: videos.vision,
          induction: videos?.induction,
        });
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching video:", error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleReorder = (categoryKey, reorderedVideos) => {
    setVideo((prev) => ({
      ...prev,
      [categoryKey]: reorderedVideos,
    }));
  };

  const handleSaveReorder = async () => {
    try {
      const response = await axios.put("/admin/video", {
        category: selectedTab,
        videos: video[selectedTab],
      });

      if (response.data.success) {
        showToast("success", "Video order updated successfully");
        setVideo((prevVideos) => ({
          ...prevVideos,
          [response.data.category]: [...response.data.updatedVideos],
        }));
        setIsOrderModalOpen(false);
      }
    } catch (error) {
      showToast("error", "Error updating video list");
      console.error("Error updating video list:", error);
    }
  };

  const handleVideoTitleUpdate = async (title, videoUrl) => {
    try {
      const response = await axios.put("/admin/video/title", {
        category: selectedTab,
        newTitle: title,
        videoUrl: videoUrl,
      });

      if (response.data.success) {
        setVideo((prevVideos) => ({
          ...prevVideos,
          [response.data.category]: [...response.data.updatedVideos],
        }));
      }
    } catch (error) {
      showToast("error", "Error updating video title");
      console.error("Error updating video title:", error);
    }
  };

  const handleVideoDelete = async (videoUrl) => {
    try {
      const response = await axios.put("/admin/video/delete", {
        category: selectedTab,
        videoUrl: videoUrl,
      });

      if (response.data.success) {
        showToast("success", "Video deleted successfully");
        setVideo((prevVideos) => ({
          ...prevVideos,
          [response.data.category]: [...response.data.updatedVideos],
        }));
      }
    } catch (error) {
      showToast("error", "Error deleting video");
      console.error("Error deleting video:", error);
    }
  };

  const handleUpload = async () => {
    if (!selectedLocation)
      return showToast("error", "Please select a upload location");
    if (!selectedCategory)
      return showToast("error", "Please select a category");
    if (!title || title.trim() === "")
      return showToast("error", "Please enter a title");
    if (title.length > 50)
      return showToast("error", "Title should be less than 50 characters");
    if (!file) return showToast("error", "Please select a video to upload");

    setUploading(true);
    setError("");
    setProgress(0);

    const formData = new FormData();
    formData.append("video", file);
    formData.append("category", selectedCategory); // Add category
    formData.append("title", title); // Add title

    if (selectedLocation === "contractor") {
      formData.append("notes", notes); // Add notes if contractor
    }

    try {
      // Step 1: Upload video
      const { data } = await axios.post("/admin/video", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!data.uploadId) throw new Error("Upload ID missing!");

      // Step 2: Start listening for progress
      const eventSource = new EventSource(
        `${process.env.REACT_APP_BACKEND_URL}/admin/video/progress?uploadId=${data.uploadId}`,
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        const { success, category, progress, title, videoUrl } = JSON.parse(
          event.data
        );

        if (progress !== undefined) setProgress(progress);
        if (success) {
          showToast("success", "Video uploaded successfully");
          setVideo((prevVideos) => ({
            ...prevVideos,
            [category]: [...prevVideos[category], { title, videoUrl }],
          }));

          setIsModalOpen(false);
          setFile(null);
          setSelectedCategory("");
          setTitle("");
          setNotes("");
          setProgress(0);
          setUploading(false);
          eventSource.close(); // Stop listening once complete
        }
      };

      eventSource.onerror = () => eventSource.close();
    } catch (err) {
      showToast("error", "Upload failed. Try again.");
      setUploading(false);
      setError("Upload failed. Try again.");
      console.error("Upload error:", err);
      setFile(null);
      setSelectedCategory("");
      setTitle("");
      setNotes("");
      setProgress(0);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container mx-auto p-4 max-w-lg">
      <Button onClick={() => setIsModalOpen(true)} className="black-button">
        Upload Video
      </Button>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Upload a Video"
        className="max-w-2xl"
      >
        {/* Select Dropdown */}
        {/* First Dropdown: Select where to upload */}
        <div className="mt-4">
          <label className="form-label">Select where to upload</label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="form-select border border-mediumgray p-2 min-h-[20px] mt-2"
          >
            <option value="">-- Select where to upload --</option>
            <option value="agent">Agent</option>
            <option value="contractor">Contractor</option>
          </select>
        </div>

        {/* Select Category Dropdown */}
        <div className="mt-4">
          <label className="form-label">Select Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="form-select border border-mediumgray p-2 min-h-[20px] mt-2"
          >
            <option value="">-- Select Category --</option>
            {selectedLocation === "agent" && (
              <>
                <option value="startWithBelief">Start with Belief</option>
                <option value="beleef">Beleef</option>
                <option value="vision">Vision</option>
              </>
            )}
            {selectedLocation === "contractor" && (
              <>
                <option value="induction">Induction</option>
              </>
            )}
          </select>
        </div>

        <div className="col-span-6 flex flex-col justify-between">
          <label className="form-label">Title</label>
          <input
            type="text"
            className="form-input border border-mediumgray"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={50}
          />
        </div>
        {selectedLocation === "contractor" && (
          <div className="col-span-6 flex flex-col justify-between">
            <label className="form-label">Notes</label>
            <input
              type="text"
              className="form-input border border-mediumgray"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={100}
            />
          </div>
        )}

        {/* Drag & Drop Area */}
        <div
          className="border-2 border-dashed border-gray-400 p-6 rounded-lg text-center cursor-pointer bg-gray-100 hover:bg-gray-200 transition-all"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <p className="text-gray-500">Drag & Drop a Video Here</p>
          <p className="text-sm text-gray-400">or</p>
          <label className="cursor-pointer bg-gray-500 text-white px-4 py-2 rounded-md mt-2 inline-block">
            Select Video
            <input
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        {/* Show Selected Video Preview */}
        {file && (
          <div className="mt-4 flex flex-col items-center ">
            <p className="text-sm text-gray-600">{file.name}</p>
            <video
              src={videoPreviewUrl}
              controls
              className="h-96 rounded-md mt-2"
            />
          </div>
        )}

        {/* Upload Button */}
        <div className="flex flex-col items-center">
          <Button
            onClick={handleUpload}
            disabled={uploading}
            loading={uploading}
            className="w-fit bg-black text-white px-4 py-2 rounded-md  disabled:bg-gray-400"
          >
            Upload Video
          </Button>

          {uploading && progress >= 0 && progress <= 100 && (
            <div className="w-full bg-gray-200 rounded-md mt-2">
              <div
                className="bg-black text-xs font-bold text-white text-center p-1 rounded-md"
                style={{ width: `${progress}%` }}
              >
                {progress}%
              </div>
            </div>
          )}

          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      </Modal>

      <Modal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        title="Change Order"
      >
        <ReorderVideo
          videos={video}
          handleReorder={handleReorder}
          handleSaveReorder={handleSaveReorder}
          selectedCategory={selectedTab}
          handleVideoDelete={handleVideoDelete}
          handleVideoTitleUpdate={handleVideoTitleUpdate}
        />
      </Modal>

      {/* Uploaded Videos */}
      <h3 className="text-lg font-semibold mt-6">Uploaded Videos</h3>
      {/* <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-8">
        {videos.length === 0 ? (
          <p className="col-span-full text-center">No videos uploaded yet.</p>
        ) : (
          videos.map((videoUrl, index) => (
            <div key={index} className="w-[15rem] h-[25rem]  rounded-md ">
              <video controls className="h-full">
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ))
        )}
      </div> */}
      <ShowVideos videos={video} openReorderModal={openReorderModal} />
    </div>
  );
};

export default Videos;
